import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { X, Calendar, Clock, Loader2, Send, Headphones, Building2 } from 'lucide-react';

const SERVICE_TYPES = [
  { value: 'recording', label: 'Recording Session' },
  { value: 'mixing', label: 'Mixing' },
  { value: 'mastering', label: 'Mastering' },
  { value: 'studio_time', label: 'Studio Time' },
  { value: 'general', label: 'General Inquiry' },
];

export default function BookingModal({ targetId, targetName, targetRole, services, onClose }) {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_type: '',
    service_id: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
    notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Please log in to send a booking request');
      return;
    }
    if (!form.service_type && !form.service_id) {
      toast.error('Please select a service type');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/creatives/${targetId}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          type: form.service_type || 'general',
          service_type: form.service_type,
          service_id: form.service_id || null,
          preferred_date: form.preferred_date || null,
          preferred_time: form.preferred_time || null,
          message: form.message,
          notes: form.notes,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to send request');
      }
      toast.success(`Booking request sent to ${targetName}!`);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const RoleIcon = targetRole === 'studio' ? Building2 : Headphones;

  return (
    <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <Card
        className="glass-card border-border w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-modal"
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center">
                <RoleIcon className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold">Book {targetName}</h2>
                <p className="text-xs text-muted-foreground capitalize">Request a {targetRole} session</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-booking-modal">
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Service Type */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Service Type *</label>
              <Select value={form.service_type} onValueChange={(v) => setForm(f => ({ ...f, service_type: v }))}>
                <SelectTrigger data-testid="booking-service-type">
                  <SelectValue placeholder="Select a service..." />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* If provider has specific services listed */}
            {services && services.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-1.5 block">Specific Service (optional)</label>
                <Select value={form.service_id} onValueChange={(v) => setForm(f => ({ ...f, service_id: v }))}>
                  <SelectTrigger data-testid="booking-specific-service">
                    <SelectValue placeholder="Choose from their services..." />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.service_id} value={s.service_id}>
                        {s.title}{s.price != null ? ` — $${s.price}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Date + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block flex items-center gap-1"><Calendar className="w-3 h-3" />Preferred Date</label>
                <Input
                  type="date"
                  value={form.preferred_date}
                  onChange={(e) => setForm(f => ({ ...f, preferred_date: e.target.value }))}
                  data-testid="booking-date"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block flex items-center gap-1"><Clock className="w-3 h-3" />Preferred Time</label>
                <Input
                  type="time"
                  value={form.preferred_time}
                  onChange={(e) => setForm(f => ({ ...f, preferred_time: e.target.value }))}
                  data-testid="booking-time"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Message</label>
              <Textarea
                placeholder="Describe what you're looking for..."
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                rows={3}
                data-testid="booking-message"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">Additional Notes</label>
              <Textarea
                placeholder="Any specific requirements, references, or links..."
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                data-testid="booking-notes"
              />
            </div>

            {/* Submit */}
            <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-booking-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {submitting ? 'Sending...' : 'Send Booking Request'}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}
